import React, { useEffect, useMemo } from "react";

import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/types/entity";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import { LoadingSpinner, Modal } from "@mapmycustomers/ui";

import createEmailAssociatedEntity from "@app/util/email/createEmailAssociatedEntity";

import EmailPreviewView from "../../EmailPreviewView";
import EmailRecipientSelect from "../../EmailRecipientSelect";
import EmailContext from "../EmailContext";

import styles from "./EmailPreviewModal.module.scss";
import messages from "./messages";

const EmailPreviewModal = () => {
  const intl = useIntl();

  const {
    attachedFiles,
    body,
    entities,
    onPreviewCreate,
    onPreviewHide,
    onPreviewRecipientIdChange,
    preview,
    previewLoading,
    previewOpen,
    previewRecipientId,
    subject,
  } = useNonNullableContext(EmailContext);

  const selectedRecipient = useMemo(
    () => entities.find((recipient) => recipient.id === previewRecipientId),
    [entities, previewRecipientId]
  );

  useEffect(() => {
    if (previewOpen && previewRecipientId) {
      onPreviewCreate({
        associatedEntities: [createEmailAssociatedEntity({ entityId: previewRecipientId })],
        body: body,
        entityType: entities[0]?.entity ?? EntityType.COMPANY,
        isHtml: true,
        logActivity: false,
        subject: subject,
        tracking: true,
      });
    }
  }, [body, onPreviewCreate, entities, previewOpen, previewRecipientId, subject]);

  return (
    <Modal
      className={styles.modal}
      closable
      destroyOnClose
      footer={null}
      onCancel={onPreviewHide}
      open={previewOpen}
      title={intl.formatMessage(messages.previewAs)}
      width="48rem"
    >
      <div className={styles.preview}>
        {preview && selectedRecipient ? (
          <EmailPreviewView
            attachedFiles={attachedFiles}
            body={preview.html.body}
            recipient={selectedRecipient}
            subject={preview.html.subject}
          />
        ) : (
          <div className={styles.noPreview}>
            {previewLoading && <LoadingSpinner />}
            <span className={styles.noPreviewTitle}>
              {intl.formatMessage(messages.previewWillShowHere)}
            </span>
            <span className={styles.noPreviewDescription}>
              {intl.formatMessage(messages.chooseRecipient)}
            </span>
          </div>
        )}
      </div>
      <div className={styles.selectorContainer}>
        <div className={styles.previewAs}>{intl.formatMessage(messages.previewAs)}</div>
        <EmailRecipientSelect
          disabled={previewLoading}
          onChange={onPreviewRecipientIdChange}
          recipients={entities}
          value={previewRecipientId}
        />
        <div className={styles.previewAs}>{intl.formatMessage(messages.previewAsDescription)}</div>
      </div>
    </Modal>
  );
};

export default EmailPreviewModal;
